import { Facet, FacetDetail } from "@/types/productListing";
import styles from "./FilterDropdown.module.scss";
import classNames from "classnames/bind";
import { useState, memo, useMemo } from "react";
import { FacetItem } from "@/components/Facets/components/FacetItem/FacetItem";
import { FacetSearch } from "@/components/Facets/components/FacetSearch/FacetSearch";
import useRouteLoading from "@/hooks/useRouteLoading";

const cx = classNames.bind(styles);

const SEARCH_THRESHOLD = 21;

// Color dimension names that should use two-column layout
const COLOR_DIMENSIONS = [
  "skus.basicColor",
  "childSKUs.basicColor",
  "product.basicColor",
];

type FilterDropdownProps = {
  facet: Facet;
  isOpen: boolean;
  dispatchFacetAnalytics: (
    eventType: string,
    facetName: string,
    facetDetails: FacetDetail,
  ) => void;
};

export const FilterDropdown = memo(function FilterDropdown({
  facet,
  isOpen,
  dispatchFacetAnalytics,
}: FilterDropdownProps) {
  const [searchTerm, setSearchTerm] = useState("");
  const isRouteLoading = useRouteLoading();

  // Filter facet details based on search term
  const filteredDetails = useMemo(() => {
    if (!searchTerm) return facet.details;

    const lowerSearchTerm = searchTerm.toLowerCase();
    return facet.details.filter((detail) =>
      detail.label?.toLowerCase().includes(lowerSearchTerm),
    );
  }, [facet.details, searchTerm]);

  const isFacetSearchable = facet.details.length >= SEARCH_THRESHOLD;
  const isColorSwatch = COLOR_DIMENSIONS.includes(facet.dimensionName);

  if (!isOpen) return null;

  return (
    <div
      className={cx("filterDropdown", {
        "u-loading": isRouteLoading,
      })}
      data-testid={`filter-dropdown-${facet.dimensionName}`}
      role="dialog"
      aria-label={`${facet.name} filters`}
      aria-busy={isRouteLoading}
    >
      <div className={styles.dropdownContent}>
        {isFacetSearchable && (
          <div className={styles.searchSection}>
            <FacetSearch
              label={facet.name}
              facetSearchValue={searchTerm}
              setFacetSearchValue={setSearchTerm}
              isFilterDrawer={true}
            />
          </div>
        )}

        <div
          className={cx("facetList", {
            "facetList--twoColumns": isColorSwatch,
            "facetList--disabled": isRouteLoading,
          })}
        >
          {filteredDetails.map((detail) => (
            <FacetItem
              key={detail.label}
              facetDetails={detail}
              facetName={facet.name}
              dimensionName={facet.dimensionName}
              dispatchFacetAnalytics={dispatchFacetAnalytics}
              className={styles.facetItem}
              disabled={isRouteLoading}
            />
          ))}
        </div>
      </div>
    </div>
  );
});
